import React from 'react';
import { CHOICE_INDICATOR } from '../../common';
import { styled, css, ThemeStyledProps } from '@shared/joykit/packages/core/common/styledComponents';
import { animationTransition } from '../../utils';
import type { CheckboxV1Props } from './checkbox.types';
import { IconV1 } from '../IconV1';

// Color constants matching V2 styling (mono palette)
const COLORS = {
  mono2: '#F7F7F7', // disabled background
  mono3: '#EBEBEB', // disabled border
  mono5: '#D9D9D9', // default border
  mono8: '#949494', // hover border
  mono14: '#333333', // checked background
  white: '#FFFFFF', // checkmark color
} as const;

const themedStyle = (props: ThemeStyledProps<Pick<CheckboxV1Props, 'disabled'>>) => {
  if (props.disabled) {
    return css`
      background-color: ${COLORS.mono2};
      border-color: ${COLORS.mono3};
      color: ${COLORS.mono3};
    `;
  }
  return css`
    color: ${COLORS.mono14};
  `;
};

export const StyledCheckIcon = styled(IconV1)`
  transition: ${animationTransition('opacity')};
`;

export const CheckContainer = styled(({ disabled, children, currentColor, className, ...restProps }) => {
  return (
    <span
      {...restProps}
      className={className}
    >
      {children}
    </span>
  );
})<{ disabled?: boolean }>`
  height: 1.25rem;
  width: 1.25rem;
  border: 1px solid ${COLORS.mono5};
  border-radius: 4px;
  position: relative;
  background-color: ${COLORS.white};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: ${animationTransition('border-color', 'background-color', 'color', 'box-shadow')};
  ${themedStyle}
`;

const Input: React.FC<CheckboxV1Props> = ({ children, alignIndicator, label, defaultChecked, hideLabel, ...props }) => (
  <input
    aria-checked={props.checked}
    onChange={props.onChange}
    {...props}
    defaultChecked={defaultChecked}
    type={'checkbox'}
  />
);

export const StyledInput = styled(Input)`
  :enabled {
    :hover ~ .${CHOICE_INDICATOR} {
      border-color: ${COLORS.mono8};
    }
    :focus ~ .${CHOICE_INDICATOR} {
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.15);
    }
    :active ~ .${CHOICE_INDICATOR} {
      border-color: ${COLORS.mono8};
    }
  }

  & ~ ${CheckContainer} ${StyledCheckIcon} {
    opacity: 0;
  }

  &:checked ~ ${CheckContainer} {
    background-color: ${COLORS.mono14};
    border-color: transparent;
    color: ${COLORS.white};
  }

  &:checked ~ ${CheckContainer} ${StyledCheckIcon} {
    opacity: 1;
  }
`;
